/**
 * Choosing a person, unambiguously.
 *
 * HQ-HR-IDENTITY-001 and HQ-OFFBOARD-SELECTION-001. Discipline, Safety,
 * Background Checks and Offboarding each rendered their employee list as
 * `{e.first_name ?? e.email}` — so nine people called "QA" produced nine
 * identical options, and the selected value was a hidden id. An administrator
 * could file a disciplinary record, log an injury, order a background check or
 * begin deactivating the wrong person and have no way to notice.
 *
 * These are the highest-consequence forms in HQ. They are also the ones where a
 * first name is least likely to be unique — a warehouse with two Davids is
 * ordinary, and the screen showed "David" twice.
 *
 * So: full name, and enough beside it to tell two people apart. The label picks
 * what actually distinguishes them rather than showing everything always, since
 * a list where every row carries an email is a list nobody reads.
 */

interface PersonLike {
  id: string;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  employee_number?: string | null;
  job_title?: string | null;
  department?: string | null;
  status?: string | null;
}

/** "Dana Whitfield" — or the email, when there is no name to show. */
export function personName(p: PersonLike): string {
  const name = [p.first_name, p.last_name].filter(Boolean).join(" ").trim();
  return name || p.email || "Unknown";
}

/**
 * A label that separates this person from the others in the same list.
 *
 * Only adds a qualifier when the displayed name is shared, so an unambiguous
 * list stays readable. Email is the last resort and always unique.
 */
export function distinguishingLabel(p: PersonLike, all: PersonLike[]): string {
  const name = personName(p);
  const sharers = all.filter(o => personName(o) === name);
  if (sharers.length <= 1) return name;

  const qualifier =
    p.employee_number ? `#${p.employee_number}`
    : p.job_title ? p.job_title
    : p.department ? p.department
    : p.email ? p.email
    : p.id.slice(0, 8);
  return `${name} — ${qualifier}`;
}

/**
 * A <select> over people, labelled so two of them cannot be confused.
 *
 * Deliberately a select rather than a search box: these lists are tens of
 * people, not thousands, and replacing a familiar control on the disciplinary
 * form is a bigger change than the finding calls for.
 */
export function EmployeePicker({
  people, value, onChange, label, placeholder = "— Select —", required, disabled, className,
}: {
  people: PersonLike[];
  value: string;
  onChange: (id: string) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
}) {
  const selected = people.find(p => p.id === value);
  return (
    <div className={className}>
      {label && <label className="label">{label}</label>}
      <select
        className="input"
        value={value}
        required={required}
        disabled={disabled}
        aria-label={label ?? "Employee"}
        onChange={e => onChange(e.target.value)}
      >
        <option value="">{placeholder}</option>
        {people.map(p => (
          <option key={p.id} value={p.id}>{distinguishingLabel(p, people)}</option>
        ))}
      </select>
      {/* Says back who was chosen. On a form that ends someone's employment,
          reading the name a second time is worth one line. */}
      {selected && (
        <p className="mt-1 text-xs text-gray2">
          {personName(selected)}
          {selected.email ? ` · ${selected.email}` : ""}
          {selected.job_title ? ` · ${selected.job_title}` : ""}
        </p>
      )}
    </div>
  );
}
